import { createSystem, defaultConfig, defineTokens, SystemStyleObject } from '@chakra-ui/react'

export const theme = defineTokens({
  colors: {
    white: { value: '#ffffff' },
    black: { value: '#02110C' },
    light: { value: '#F3F7F7' },
    gold: { value: '#F29500' },
    gray: {
      a100: { value: '#E7EFEF' },
      a300: { value: '#808080' },
      a500: { value: '#414C49' },
    },
    green: {
      500: { value: '#4E8278' },
      a25: { value: '#F3F7F7' },
      a50: { value: '#E8F0EF' },
      a100: { value: '#D3E2E0' },
      a125: { value: '#CCDEDB' },
      a135: { value: '#AFC8C5' },
      a175: { value: '#B0CCC7' },
      a150: { value: '#8DB6AF' },
      a200: { value: '#4E8278' },
      a250: { value: '#234E45' },
      a300: { value: '#ACD2BD' },
    },
    red: {
      a50: { value: '#F0E8E8' },
      a75: { value: '#E9BAB7' },
      a80: { value: '#F36262' },
      a100: { value: '#D98C87' },
      a125: { value: '#D58883' },
      a150: { value: '#B41A0F' },
      a200: { value: '#824E4E' },
    },
    text: {
      header: { value: '#1A202C' },
      sort: { value: '#0E0D0D' },
    },
    leid: {
      lysGul: { value: '#FCEFC2' },
      lysGulLight: { value: '#FEF2C4' },
      gul: { value: '#F5C736' },
      gulLight: { value: '#f7c938' },
      gr: { value: '#A6A6A6' },
      sort: { value: '#0E0D0D' },
      lysGrnn: { value: '#C7E0D2' },
      grnn: { value: '#ACD2BD' },
      lighterGrnn: { value: '#EDF5F1' },
      lightGrnn: { value: '#DBEDE3' },
      mrkGrnn: { value: '#4E8278' },
      mrkereGrnn: { value: '#226C5D' },
      drkGrnn: { value: '#2F3C33' },
      disabled: { value: '#E6E5E5' },
      lightGrey: { value: '#EDEDED' },
      grey: { value: '#CECCCC' },
      darkGrey: { value: '#898787' },
    },
    updatedLeid: {
      peach: { value: '#F5C736' },
      danger: { value: '#DC2626' },
      success: { value: '#008925' },
      green: { value: '#294828' },
      lightGreen: { value: '#BBD4C7' },
      teal: { value: '#093849' },
      lightTeal: { value: '#8FADB9' },
      brick: { value: '#452322' },
      lightBrick: { value: '#E6B1AC' },
      earth: { value: '#503F2F' },
      lightEarth: { value: '#E6CB86' },
    },
    leidTheme: {
      20: { value: '#FBFBFB' },
      50: { value: '#F3F7F7' },
      100: { value: '#E8F0EF' },
      200: { value: '#D3E2E0' },
      300: { value: '#CCDEDB' },
      400: { value: '#AFC8C5' },
      500: { value: '#B0CCC7' },
      600: { value: '#8DB6AF' },
      700: { value: '#4E8278' },
      800: { value: '#ACD2BD' },
      900: { value: '#ACD2BD' },
      950: { value: '#ACD2BD' },
    },
  },
  borderRadius: {
    extraSmall: { value: '6px' },
    small: { value: '9px' },
    big: { value: '12px' },
    extraBig: { value: '24px' },
  },
  radii: {
    extraSmall: { value: '6px' },
    small: { value: '9px' },
    big: { value: '12px' },
    extraBig: { value: '24px' },
  },
  fonts: {
    main: { value: 'poppins, sans-serif' },
    body: { value: 'poppins, sans-serif' },
    heading: { value: 'poppins, sans-serif' },
  },
  fontSizes: {
    xxs: { value: '1rem' },
    xs: { value: '1.2rem' },
    sm: { value: '1.4rem' },
    smd: { value: '1.6rem' },
    md: { value: '1.8rem' },
    lg: { value: '2.4rem' },
    xl: { value: '2.8rem' },
    '2xl': { value: '4.2rem' },
    '3xl': { value: '4.8rem' },
  },
  breakpoints: {
    sm: { value: '475px' },
    md: { value: '768px' },
    lg: { value: '1024px' },
    xl: { value: '1440px' },
    '2xl': { value: '2560px' },
  },
  zIndices: {
    hide: -1,
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipNav: 1600,
    toast: 1700,
    tooltip: 1800,
    max: 2147483647,
  },
  sizes: {
    '110': { value: '110%' },
  },
  components: {
    Modal: {
      sizes: {
        xl: { value: '1000px' },
      },
    },
  },
})

const semanticTokens = {
  colors: {
    leidTheme: {
      solid: { value: '{colors.leidTheme.700}' },
      contrast: { value: '{colors.white}' },
      fg: { value: '{colors.leidTheme.700}' },
      muted: { value: '{colors.leidTheme.100}' },
      subtle: { value: '{colors.leidTheme.200}' },
      emphasized: { value: '{colors.leidTheme.300}' },
      focusRing: { value: '{colors.leidTheme.500}' },
    },
  },
}

const globalCss: Record<string, SystemStyleObject> = {
  html: {
    boxSizing: 'border-box',
    height: '100%',
    fontSize: '62.5%',
    scrollBehavior: 'smooth',
    WebkitTapHighlightColor: 'transparent',
  },
  '*, *::before, *::after': {
    boxSizing: 'inherit',
  },
  body: {
    margin: 0,
    padding: 0,
    fontStyle: 'normal',
    fontFamily: 'poppins, sans-serif',
    fontSize: '1.6rem',
    lineHeight: 1.5,
    backgroundColor: 'white',
  },
  ':focus': {
    outline: 'none',
  },
}

export default createSystem(defaultConfig, {
  theme: { tokens: theme, semanticTokens },
  globalCss,
})
